import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { interviewIqApi } from './api';
import QuestionRecorder from './QuestionRecorder';
import EvaluationLoader from './EvaluationLoader';
import ScoreBreakdown from './ScoreBreakdown';
import BadgeShelf from './BadgeShelf';
import InterviewIQLayout from './InterviewIQLayout';

export default function InterviewIQDeckSessionPage() {
  const { deckNumber } = useParams();

  const [deckData, setDeckData] = useState(null);
  const [step, setStep] = useState('overview');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [resultsPayload, setResultsPayload] = useState(null);
  const [rewardBadge, setRewardBadge] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadDeck = async () => {
      try {
        const data = await interviewIqApi.getDeck(deckNumber);
        setDeckData(data);
      } catch (err) {
        setError(err.message || 'Unable to load deck.');
      }
    };
    loadDeck();
  }, [deckNumber]);

  const questions = useMemo(() => deckData?.questions || [], [deckData]);

  const pollResponseUntilDone = async (responseId) => {
    for (let i = 0; i < 50; i += 1) {
      const response = await interviewIqApi.getResponse(responseId);
      if (response.status === 'completed' || response.status === 'failed') {
        return response;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    return interviewIqApi.getResponse(responseId);
  };

  const startDeck = async () => {
    try {
      await interviewIqApi.startDeck(deckNumber);
      setStep('question');
    } catch (err) {
      setError(err.message || 'Unable to start deck.');
    }
  };

  const handleRecorded = async (blob) => {
    try {
      setStep('evaluating');
      const question = questions[activeQuestionIndex];
      const upload = await interviewIqApi.uploadResponse({
        recordingBlob: blob,
        questionId: question.questionId,
        deckId: deckData?.deck?.deckId,
        deckNumber,
      });

      const evaluated = await pollResponseUntilDone(upload.responseId);
      setResponses((prev) => [...prev, evaluated]);

      if (activeQuestionIndex < questions.length - 1) {
        setActiveQuestionIndex((prev) => prev + 1);
        setStep('question');
        return;
      }

      const deckResults = await interviewIqApi.getDeckResults(deckNumber);
      setResultsPayload(deckResults);
      if (Array.isArray(deckResults?.newBadges) && deckResults.newBadges.length > 0) {
        setRewardBadge(deckResults.newBadges[0]);
      }
      setStep('results');
    } catch (err) {
      setError(err.message || 'Failed during recording upload/evaluation.');
      setStep('question');
    }
  };

  const handleRestartQuestion = () => {
    // Simply stay on the same question, QuestionRecorder will reset itself
    setError('');
  };

  const handleGoBackFromQuestion = () => {
    if (activeQuestionIndex > 0) {
      setActiveQuestionIndex((prev) => prev - 1);
      setError('');
    } else {
      setStep('overview');
      setActiveQuestionIndex(0);
      setResponses([]);
      setError('');
    }
  };

  return (
    <InterviewIQLayout>
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-10" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="max-w-5xl mx-auto space-y-5">
        <header className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-xs uppercase text-orange-300">InterviewIQ</p>
              <h1 className="text-2xl font-bold text-stone-100">Deck {deckNumber}</h1>
            </div>
            <Link to="/interviewiq" className="text-sm text-stone-300 border border-white/20 rounded-lg px-3 py-2 text-center">Back to Dashboard</Link>
          </div>
        </header>

        {error && <div className="rounded-xl border border-rose-300/30 bg-rose-500/10 p-4 text-rose-200">{error}</div>}

        {!deckData ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-stone-300">Loading deck...</div>
        ) : null}

        {deckData && step === 'overview' ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <p className="text-stone-100 font-semibold">Deck Overview</p>
            <p className="text-sm text-stone-400 mt-1">3 questions will be asked: 30s prep + 2m recording each.</p>

            <div className="grid md:grid-cols-3 gap-3 mt-4">
              {(deckData.preview || []).map((q) => (
                <div key={q.questionId} className="rounded-xl border border-white/10 bg-black/30 p-3">
                  <p className="text-stone-200 text-sm">{q.type.toUpperCase()}</p>
                  <p className="text-orange-300 text-xs mt-1">{q.difficulty.toUpperCase()}</p>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={startDeck}
              className="mt-5 w-full sm:w-auto px-5 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-400 text-black font-semibold"
            >
              Start Session
            </button>
          </div>
        ) : null}

        {deckData && step === 'question' && questions[activeQuestionIndex] ? (
          <QuestionRecorder
            key={`question-${activeQuestionIndex}-${Date.now()}`}
            question={questions[activeQuestionIndex]}
            onRecorded={handleRecorded}
            onError={(message) => setError(message)}
            onRestart={handleRestartQuestion}
            onGoBack={handleGoBackFromQuestion}
          />
        ) : null}

        {step === 'evaluating' ? <EvaluationLoader /> : null}

        {step === 'results' ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-orange-500/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-emerald-200 text-sm uppercase tracking-wide">
                    {resultsPayload?.deck?.status === 'completed' ? '✓ Deck Completed' : 'Deck In Progress'}
                  </p>
                  <p className="text-stone-100 text-3xl font-bold mt-1">
                    {resultsPayload?.totalDeckScore ? `${resultsPayload.totalDeckScore}/10` : '-/10'}
                  </p>
                </div>
                <div className="text-5xl">
                  {resultsPayload?.deck?.status === 'completed' ? '🎉' : '📊'}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                <div className="text-center">
                  <p className="text-xs text-stone-400 uppercase">Questions</p>
                  <p className="text-lg font-semibold text-stone-100">{(resultsPayload?.responses || responses).length}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-stone-400 uppercase">Avg Score</p>
                  <p className="text-lg font-semibold text-orange-300">
                    {resultsPayload?.totalDeckScore ? resultsPayload.totalDeckScore.toFixed(1) : '-'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-stone-400 uppercase">Status</p>
                  <p className="text-lg font-semibold text-emerald-300">
                    {resultsPayload?.deck?.status === 'completed' ? 'Pass' : 'Review'}
                  </p>
                </div>
              </div>

              {resultsPayload?.deck?.status !== 'completed' && (
                <div className="mt-4 p-3 rounded-lg bg-orange-500/10 border border-orange-400/20">
                  <p className="text-xs text-orange-200">
                    💡 Keep practicing! Complete more questions to improve your deck score.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-stone-100 font-semibold mb-3">Individual Question Ratings</p>
              <div className="space-y-3">
                {(resultsPayload?.responses || responses).map((response) => (
                  <ScoreBreakdown key={response.responseId} response={response} />
                ))}
              </div>
            </div>

            <BadgeShelf badges={resultsPayload?.badges || []} />
          </div>
        ) : null}

        {rewardBadge ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/75" onClick={() => setRewardBadge(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-orange-300/40 bg-zinc-950 p-6 text-center shadow-2xl">
              <p className="text-xs uppercase tracking-widest text-orange-300">Reward Unlocked</p>
              <p className="mt-3 text-5xl">{rewardBadge.icon || '🏅'}</p>
              <p className="mt-3 text-2xl font-bold text-stone-100">{rewardBadge.name || 'New Badge'}</p>
              <p className="mt-2 text-sm text-stone-300">{rewardBadge.description || 'Great work. Keep going.'}</p>
              <button
                type="button"
                onClick={() => setRewardBadge(null)}
                className="mt-5 w-full rounded-lg bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-2 font-semibold text-black"
              >
                Awesome
              </button>
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </InterviewIQLayout>
  );
}
